import * as STD from '@emersya/mpa-std';
import * as CM from '@emersya/mpa-components-manager';

export interface IInputComponentConfiguration<
	TValue,
	TValueSerialized
> extends CM.DOMComponent.IConfiguration {
	defaultValue?: TValue | TValueSerialized | null;
	isEditable?: boolean;
	/**
	 * MPAEngine do generic auto serialization of inputs components to make generic replay key in hand but some components should not be handled by the system, so you can use these parameters.
	 */
	disableMPAEngineAutoSerialization?: boolean;
	disableMPAEngineAutoDeserialization?: boolean;
};
const defaultValueMarker = Symbol('defaultValue');

export abstract class InputComponent<
	TNode extends HTMLElement | null,
	TValue,
	TValueSerialized,
	TComponent extends InputComponent<TNode, TValue, TValueSerialized, TComponent, TConfiguration>,
	TConfiguration extends IInputComponentConfiguration<TValue, TValueSerialized>
> extends CM.DOMComponent<
	TNode,
	TComponent,
	TConfiguration
> {
	isEditable: boolean = true;

	static getInputEventSubType() {
		return this.type;
	};

	static override initialize() {
		CM.Component.initialize.call(this);
		this.declareEventType('value', this.getInputEventSubType());
	};

	override async component_initialize() {
		await super.component_initialize();
		this.defaultValue = await this.deserialize(
			this.component_configuration.defaultValue ?? null
		);
	};

	static isDefaultValueMarker(marker: Symbol) {
		return (marker === defaultValueMarker);
	};

	value: TValue | null = null;
	defaultValue: TValue | null = null;
	isDefaultValueApplied = false;

	override component_hasValue() {
		return (true);
	};

	async applyDefault() {
		if (this.isDefaultValueApplied) {
			this.component_throwUserError('Default already applied!', {});
		}
		if (this.defaultValue === null) {
			this.component_throwLibraryError('ApplyDefault called on a component without default!', {});
		}
		await this.updateValue(this.defaultValue, defaultValueMarker);
		this.isDefaultValueApplied = true;
	};

	setEditable(value: boolean, reason?: STD.translationsManager.TranslationMapping) {
		this.isEditable = value;
		if (!this.component_DOM) return;
		const hasTranslation = this.component_hasTranslation('notEditableReason');
		if (this.isEditable) {
			this.component_DOM.classList.remove('notEditable');
			this.component_DOM.removeAttribute('disabled');
			if (hasTranslation) {
				if (this.component_hasTranslation('title-attr')) {
					this.component_DOM.dataset.translationAttr = 'title:' + this.component_getTranslationId('title-attr');
					STD.translationsManager.update(this.component_DOM);
				} else {
					delete this.component_DOM.dataset.translationAttr;
					this.component_DOM.removeAttribute('title');
				}
			}
		} else {
			this.component_DOM.classList.add('notEditable');
			this.component_DOM.setAttribute('disabled', '');
			let translationId;
			if (reason) {
				translationId = this.component_defineTranslation('notEditableReason', reason);
			} else {
				if (!hasTranslation) {
					this.component_throwUserError('No reason', {});
				}
				translationId = this.component_getTranslationId('notEditableReason');
			}
			this.component_DOM.dataset.translationAttr = 'title:' + translationId;
			STD.translationsManager.update(this.component_DOM);
		}
	};

	override async component_onActivationChange(event: STD.events.Event<any, CM.Component<any, any>>) {
		await super.component_onActivationChange(event);
		if (event.subType === 'enabled') {
			if (
				!this.isDefaultValueApplied &&
				(this.component_configuration.defaultValue !== undefined)
			) {
				await this.applyDefault();
			}
		} else {
		}
	};

	async updateValue(
		newValue: TValue | null,
		_defaultValueMarker?: Symbol,
		force = false
	) {
		if ((this.value === newValue) && !force) {
			return;
		}
		const oldValue = this.value;
		this.value = newValue;
		const isDefaultValue = _defaultValueMarker === defaultValueMarker;
		await this.dispatchEvent('value', {
			subType: (<typeof InputComponent><any>this.static).getInputEventSubType(),
			details: {
				oldValue,
				newValue,
				isDefaultValue
			}
		});
	};

	abstract deserialize(
		value: TValue | TValueSerialized | null
	): Promise<TValue | null> | TValue | null;

	abstract serialize(): (TValueSerialized | Promise<TValueSerialized | null>) | null;

	override async component_start(startEvent: STD.events.Event<any, CM.Component<any, any>>) {
		await super.component_start(startEvent);
		if (this.component_configuration.isEditable === false) {
			this.setEditable(false);
		}
		if (!this.isDefaultValueApplied) {
			if (this.defaultValue !== null) {
				// if (!this.isDisabled) {
				await this.applyDefault();
				// }
			} else {
				this.isDefaultValueApplied = true; //set to true when there are no default value
			}
		}
	};

	abstract reset(): void | Promise<void>;

	abstract isValueTheDefault(): boolean;

	/**
	 * Return the current value or null if there are no value or the value is the default
	 **/
	getUserValue() {
		return (
			((this.value !== null) && !this.isValueTheDefault()) ? this.value :
				null
		);
	};

	async getUserValueSerialized() {
		return (
			((this.value !== null) && !this.isValueTheDefault()) ? await this.serialize() :
				null
		);
	};

	constructor(
		descriptor: ConstructorParameters<typeof CM.Component>[0],
		configuration: TConfiguration
	) {
		super(descriptor, configuration);
		if (this.component_configuration.isEditable === undefined) {
			this.component_configuration.isEditable = true;
		}
	};
};
export namespace NS {
	export const Component = InputComponent;
	export type Component<
		V extends HTMLElement | null,
		W,
		X,
		Y extends InputComponent<V, W, X, Y, Z>,
		Z extends IInputComponentConfiguration<W, X>
	> = InputComponent<V, W, X, Y, Z>;
	export type Configuration<X, Y> = IInputComponentConfiguration<X, Y>;
}
CM.Manager.declareAbstract({
	id: 'input',
	type: <any>InputComponent
});
